/** Shared maths for the dashboard — same numbers on the server and in the UI. */

/**
 * Estimated one-rep max (Epley). Above ~12 reps the formula drifts, so it is
 * clamped: a 20-rep set says more about conditioning than about maximal strength.
 */
export function estimate1rm(weightKg: number, reps: number): number {
  if (weightKg <= 0 || reps <= 0) return 0;
  const effectiveReps = Math.min(reps, 12);
  return round(weightKg * (1 + effectiveReps / 30), 1);
}

/** Total load moved by a set. */
export function setVolume(weightKg: number, reps: number): number {
  return round(weightKg * reps, 1);
}

/** Round to a plate-realistic increment (2.5 kg by default). */
export function snapToPlate(weightKg: number, increment = 2.5): number {
  return round(Math.round(weightKg / increment) * increment, 2);
}

export function round(value: number, decimals = 2): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function kgToLb(kg: number): number {
  return round(kg * 2.2046226218, 1);
}

export function lbToKg(lb: number): number {
  return round(lb / 2.2046226218, 2);
}

/** Local YYYY-MM-DD — never UTC, or a 22:00 session lands on the wrong day. */
export function toIsoDate(date: Date = new Date()): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

/** Inclusive start date for a trend range, relative to `today`. */
export function rangeStart(range: "4w" | "3m" | "6m" | "1y" | "all", today: Date = new Date()): string | null {
  const d = new Date(today);
  switch (range) {
    case "4w":
      d.setDate(d.getDate() - 28);
      break;
    case "3m":
      d.setMonth(d.getMonth() - 3);
      break;
    case "6m":
      d.setMonth(d.getMonth() - 6);
      break;
    case "1y":
      d.setFullYear(d.getFullYear() - 1);
      break;
    case "all":
      return null;
  }
  return toIsoDate(d);
}

/** Slug used to deduplicate exercises coming from free-text spreadsheet cells. */
export function slugifyExercise(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** A rep takes about three seconds; each side of a one-sided set counts its own. */
export const SECONDS_PER_REP = 3;

/** How long a set itself lasts, roughly: its reps on both sides, or the hold. */
export function estimateSetSeconds(set: {
  reps: number;
  repsRight?: number | null;
  durationSeconds?: number | null;
}): number {
  if (set.durationSeconds != null) return set.durationSeconds;
  return (set.reps + (set.repsRight ?? 0)) * SECONDS_PER_REP;
}
